import React, { useContext, useEffect, useState } from 'react';
import { skip, tap } from 'rxjs/operators';
import { useSetResolved } from '@wearejh/m2-pwa-engine/lib/router/useSetResolved';

import { CategoryQueryContext, CategoryQueryProvider } from 'src/components/CategoryListingPage/CategoryQueryProvider';
import { ProductListToolbar } from 'src/components/CategoryListingPage/ProductListToolbar';
import { ProductListWrapper } from 'src/components/CategoryListingPage/ProductListWrapper';
import { Container, TwoCol, Wrapper } from 'src/components/Layout';
import { FilterModalProvider } from 'src/components/CategoryListingPage/FilterModalContext';
import { LayeredNav } from 'src/components/LayeredNav/LayeredNav';
import {
    AggregationQueryContext,
    AggregationQueryProvider,
} from 'src/components/CategoryListingPage/AggregationQueryProvider';
import {
    ProductListQueryContext,
    ProductListQueryProvider,
} from 'src/components/CategoryListingPage/ProductListQueryProvider';
import { DefaultMeta } from 'src/components/AppShell/Head';
import { ErrorOrOfflineConnected } from 'src/components/Error/ErrorOrOffline';
import { LoaderBar, LoaderMinHeight } from 'src/components/Helpers/Skeleton';
import { Breadcrumbs } from 'src/components/Breadcrumbs/Breadcrumbs';
import { PageTitle } from 'src/components/Layout/PageTitle';
import { NostoContextProvider } from 'src/components/Nosto/NostoProvider';
import { fireGTMEvent } from 'src/features/analytics/hooks/useGtm';
import '../../scss/_root-component.scss';
import { PostsByArrayOfIDs } from 'src/wordpress/components/PostsByArrayOfIDs';
import { useLayeredNav } from 'src/components/LayeredNav/hooks/useLayeredNav';
import CurrentSelection from 'src/components/LayeredNav/CurrentSelection';
import { useBreakpointMatch } from 'src/hooks/useBreakpoint';
import { selectionToValues } from 'src/components/CategoryListingPage/utils/cleanAggregations';
import { Nosto } from 'src/components/Nosto/Nosto';
import { ProductAddToBasketModal } from 'src/components/AtbModal/ProductAddToBasketModal';

type ProductProps = {
    id: number;
};

export const CategoryProductListRoot = React.memo(function CategoryProductListRoot(props: ProductProps) {
    useSetResolved();

    React.useEffect(() => {
        fireGTMEvent({ event: 'pageview', pageType: 'Product Listing Page' });
    }, []);

    return (
        <CategoryQueryProvider id={props.id}>
            <AggregationQueryProvider id={props.id}>
                <ProductListQueryProvider id={props.id}>
                    <CategoryRootLoader />
                </ProductListQueryProvider>
            </AggregationQueryProvider>
        </CategoryQueryProvider>
    );
});

const CategoryRootLoader = React.memo(() => {
    const { loading } = useContext(CategoryQueryContext);
    const { loading: isMappedAggregationsLoading } = useContext(AggregationQueryContext);

    if (loading || isMappedAggregationsLoading) {
        return <LoaderMinHeight />;
    }

    return (
        <Wrapper element="section" variant="page-inner-with-breadcrumb" data-test-id="category-listing-page">
            <CategoryRootInner />
        </Wrapper>
    );
});

const CategoryRootInner = React.memo(() => {
    const { name, meta_title, meta_description, breadcrumbs, blogIds } = useContext(CategoryQueryContext);
    const { mappedAggregations } = useContext(AggregationQueryContext);
    const { isMob } = useBreakpointMatch();

    const { onRemove, onRemoveAll, onChange, selection$ } = useLayeredNav(mappedAggregations);
    const [selections, setValues] = useState(selectionToValues(mappedAggregations, selection$.value));
    const totalSelections = selections.length;

    useEffect(() => {
        const sub = selection$
            .pipe(
                skip(1), // because it's a BehaviorSubject, it will project the first element
                tap((selections) => {
                    setValues(selectionToValues(mappedAggregations, selections));
                }),
            )
            .subscribe();
        return () => sub.unsubscribe();
    }, [mappedAggregations, selection$]);

    const meta = {
        title: meta_title || name || 'unknown',
        description: meta_description || name || 'unknown',
    };

    const CategoryPath: any = [];

    for (let i = 0; i < breadcrumbs.length; i++) {
        CategoryPath.push(breadcrumbs[i]['name']);
    }

    return (
        <NostoContextProvider
            nostoData={{
                type: ['addToBasket', 'category'],
                categoryPath: `/${CategoryPath.join('/')}`,
            }}
            pageType="category"
        >
            <Container>
                <Breadcrumbs breadcrumbs={breadcrumbs} current={name} />
                <PageTitle title={name} variant="slim" />
                <FilterModalProvider>
                    <DefaultMeta data={meta} />
                    <TwoCol
                        left={
                            <LayeredNav
                                mappedAggregations={mappedAggregations}
                                currentFilters={
                                    <CurrentSelection
                                        mappedAggregations={mappedAggregations}
                                        selections={selections}
                                        removeAll={onRemoveAll}
                                        remove={onRemove}
                                        includeTitle={true}
                                    />
                                }
                                selections={selection$}
                                onChange={onChange}
                            />
                        }
                        main={
                            <>
                                <ProductListToolbar
                                    totalSelections={totalSelections}
                                    currentFilters={
                                        isMob && (
                                            <CurrentSelection
                                                mappedAggregations={mappedAggregations}
                                                selections={selections}
                                                removeAll={onRemoveAll}
                                                remove={onRemove}
                                            />
                                        )
                                    }
                                />
                                <CategoryProducts />
                                <PostsByArrayOfIDs postIds={blogIds} />
                            </>
                        }
                    />
                    <Nosto type="category" />
                </FilterModalProvider>
            </Container>
            <ProductAddToBasketModal />
        </NostoContextProvider>
    );
});

const CategoryProducts = React.memo(() => {
    const { loading: categoryLoading, urlKey, title } = useContext(CategoryQueryContext);
    const { loading: productsLoading, error: listError } = useContext(ProductListQueryContext);
    const { loading: aggregationsLoading, error: aggError } = useContext(AggregationQueryContext);

    if (listError || aggError) {
        return <ErrorOrOfflineConnected message="These was a problem loading this category" />;
    }

    if (productsLoading || categoryLoading || aggregationsLoading) {
        return <LoaderBar />;
    }

    return <ProductListWrapper title={title} urlKey={urlKey} />;
});

export default CategoryProductListRoot;
